/**
 * Resource that represents a Product (Masterdata).
 *
 * @name Product
 * @class
 * @augments TmrResource
 */
function ProductFactory(
  TmrTransformResourceAction,
  TmrResource,
  TmrApi,
  TmrLocalStorage,
  ItemConfiguration
) {
  'ngInject'
  let actions = {
    getByUpc: {
      url: TmrApi.endpoint('Product'),
      method: 'GET',
      isArray: false,
    },
    keys: {
      url: TmrApi.endpoint('Product.keys'),
      method: 'GET',
      isArray: true,
    },
    distinctKeys: {
      url: TmrApi.endpoint('Product.distinctKeys'),
      method: 'GET',
      isArray: true,
    },
    getImage: {
      url: TmrApi.endpoint('Product.image'),
      method: 'GET',
      isArray: false,
    },
    createProductGroup: {
      url: TmrApi.endpoint('Product.createProductGroup'),
      method: 'POST',
      isArray: false,
    },
    attributeSuggestion: {
      url: TmrApi.endpoint('Product.attributeSuggestion'),
      method: 'GET',
      isArray: true,
    },
  }

  TmrTransformResourceAction.appendResponse(actions.getByUpc, extractProduct)
  TmrTransformResourceAction.appendResponse(
    actions.distinctKeys,
    filterEmptyResult
  )

  let defaultValues = () => ({
    attributes: {},
  })

  let referencedProperties = [
    // product.itemConfiguration property will be instantiated as a ItemConfiguration resource
    { property: 'itemConfiguration', resource: ItemConfiguration },
  ]

  let Product = TmrResource('Product', actions, referencedProperties, defaultValues, true)

  // Replace actions that find attributes to simplify usage.
  let prevGetByUpc = Product.getByUpc
  let prevKeys = Product.keys
  let prevDistinctKeys = Product.distinctKeys
  Object.assign(Product, { getByItem, getByUpc, keys, distinctKeys })

  // Attributes formatting helper functions
  Object.assign(Product.prototype, {
    $attribute,
    $style,
    $material,
    $color,
    $size,
    $getImageUrl,
  })

  return Product

  /**
   * Transform response: extracts the product from the result array.
   */
  function extractProduct(response) {
    return Array.isArray(response)
      ? response.length
        ? response[0]
        : null
      : response
  }

  /**
   * Transform response: if there is only one object with no id, consider it an
   * empty response.
   */
  function filterEmptyResult(response) {
    if (response.length == 1 && !response[0].id) {
      return []
    }
    return response
  }

  /**
   * @method Product#getByItem
   */
  function getByItem(Item) {
    if (!Item.$getType || Item.$getType() != 'item') {
      throw new Error(
        'Product.getByItem(Item) action requires an Item resource'
      )
    }
    return prevGetByUpc.call(this, Item.upc)
  }

  /**
   * Find a single product by UPC (`code`).
   * Use when an {@link Epc} is available
   *
   * @method Product#getByUpc
   */
  function getByUpc(upc) {
    if (!upc) {
      throw new Error(
        'Product.getByUpc(upc) action requires a product code (upc)'
      )
    }
    return prevGetByUpc.call(this, { code: upc })
  }

  /**
   * Get a list of product attribute keys that contain search string.
   *
   * @method Product#keys
   * @param {search|object} search
   */
  function keys(search) {
    if (typeof search === 'string') {
      search = { key: search }
    }
    if (!search || !search.key) {
      throw new Error(
        'Product.keys(search) action requires a search string for key'
      )
    }
    arguments[0] = search
    return prevKeys.apply(this, arguments)
  }

  /**
   * Get a list of product attributes that contain search string in key.
   *
   * @method Product#distinctKeys
   * @param {search|object} search
   */
  function distinctKeys(search) {
    if (typeof search === 'string') {
      search = { key: search }
    }
    if (!search || !search.key) {
      throw new Error(
        'Product.distinctKeys(search) action requires a search string for key'
      )
    }
    arguments[0] = search
    return prevDistinctKeys.apply(this, arguments)
  }

  /**
   * Returns a formatted attribute of the product.
   *
   * @method Product#$attribute
   * @prop {number=} [idx=0] The index of the requested attribute
   * @returns {string}
   */
  function $attribute(idx = 0) {
    return this.attributes && this.attributes[idx]
      ? attribute(this.attributes[idx])
      : ''
  }

  /**
   * Returns the formatted style attribute of the product.
   *
   * @method Product#$style
   * @returns {string}
   */
  function $style() {
    return this.style ? attribute(this.style) : ''
  }

  /**
   * Returns the formatted material attribute of the product.
   *
   * @method Product#$material
   * @returns {string}
   */
  function $material() {
    return this.material ? attribute(this.material) : ''
  }

  /**
   * Returns the formatted color attribute of the product.
   *
   * @method Product#$color
   * @returns {string}
   */
  function $color() {
    return this.color ? attribute(this.color) : ''
  }

  /**
   * Returns the formatted size attribute of the product.
   *
   * @method Product#$size
   * @returns {string}
   */
  function $size() {
    return this.size ? attribute(this.size) : ''
  }

  /**
   * Returns a formatted attribute.
   *
   * @prop {string|null} attribute.value
   * @prop {string|null} attribute.valueDescription
   * @returns {string}
   */
  function attribute({ value = '', valueDescription = '' }) {
    if (value && valueDescription) {
      if (value == valueDescription) return value
      return `${value} (${valueDescription})`
    }
    if (value) return value
    if (valueDescription) return valueDescription
    return ''
  }

  /**
   * Returns the product's image url
   *
   * @method Product#$getImageUrl
   * @returns {string}
   */
  function $getImageUrl() {
    if (this.code) {
      const apiUrl = TmrApi.endpoint('Product.image')
      let imageUrl = apiUrl.replace(':sku', this.code)
      imageUrl += `?x-tmr-token=${TmrLocalStorage.get('authToken')}`
      return imageUrl
    } else {
      return null
    }
  }
}

export const factory = ['Product', ProductFactory]
